import { apiFetch } from "./api";

// ============================================================================
// DTOs (mirror src/Allmark.Contracts/QuoteAdmin/*.cs)
// ============================================================================

// ----------------------------------------------------------------------------
// Quote templates
// ----------------------------------------------------------------------------

export interface QuoteTemplateDto {
  quoteTemplateId: number;
  name: string;
  description: string | null;
  bodyText: string | null;
  isDefault: boolean;
  createdOn: string | null;
  createdByName: string | null;
  deleted: boolean;
}

export interface CreateQuoteTemplateRequest {
  name: string;
  description?: string | null;
  bodyText?: string | null;
  isDefault?: boolean;
}

export interface UpdateQuoteTemplateRequest {
  name: string;
  description: string | null;
  bodyText: string | null;
  isDefault: boolean;
}

// ----------------------------------------------------------------------------
// Quote T&C templates
// ----------------------------------------------------------------------------

export interface QuoteTandCsTemplateDto {
  quoteTandCsTemplateId: number;
  name: string;
  tandCsText: string;
  isDefault: boolean;
  createdOn: string | null;
  createdByName: string | null;
  deleted: boolean;
}

export interface CreateQuoteTandCsTemplateRequest {
  name: string;
  tandCsText: string;
  isDefault?: boolean;
}

export interface UpdateQuoteTandCsTemplateRequest {
  name: string;
  tandCsText: string;
  isDefault: boolean;
}

// ----------------------------------------------------------------------------
// Approval types
// ----------------------------------------------------------------------------

export interface ApprovalTypeDto {
  approvalTypeCode: string;
  description: string;
  requiresApprover: boolean;
  selfApprovalLimit: number | null;
  deleted: boolean;
  quotesUsingCount: number;
}

export interface CreateApprovalTypeRequest {
  approvalTypeCode: string;
  description: string;
  requiresApprover?: boolean;
  selfApprovalLimit?: number | null;
}

export interface UpdateApprovalTypeRequest {
  description: string;
  requiresApprover: boolean;
  selfApprovalLimit: number | null;
}

// ============================================================================
// API helpers
// ============================================================================

export const quoteTemplatesApi = {
  list: (): Promise<QuoteTemplateDto[]> =>
    apiFetch<QuoteTemplateDto[]>("/api/quote-templates"),

  get: (id: number): Promise<QuoteTemplateDto> =>
    apiFetch<QuoteTemplateDto>(`/api/quote-templates/${id}`),

  create: (body: CreateQuoteTemplateRequest): Promise<QuoteTemplateDto> =>
    apiFetch<QuoteTemplateDto>("/api/quote-templates", { method: "POST", body: JSON.stringify(body) }),

  update: (id: number, body: UpdateQuoteTemplateRequest): Promise<void> =>
    apiFetch<void>(`/api/quote-templates/${id}`, { method: "PUT", body: JSON.stringify(body) }),

  remove: (id: number): Promise<void> =>
    apiFetch<void>(`/api/quote-templates/${id}`, { method: "DELETE" }),
};

export const quoteTandCsApi = {
  list: (): Promise<QuoteTandCsTemplateDto[]> =>
    apiFetch<QuoteTandCsTemplateDto[]>("/api/quote-tandcs"),

  get: (id: number): Promise<QuoteTandCsTemplateDto> =>
    apiFetch<QuoteTandCsTemplateDto>(`/api/quote-tandcs/${id}`),

  create: (body: CreateQuoteTandCsTemplateRequest): Promise<QuoteTandCsTemplateDto> =>
    apiFetch<QuoteTandCsTemplateDto>("/api/quote-tandcs", { method: "POST", body: JSON.stringify(body) }),

  update: (id: number, body: UpdateQuoteTandCsTemplateRequest): Promise<void> =>
    apiFetch<void>(`/api/quote-tandcs/${id}`, { method: "PUT", body: JSON.stringify(body) }),

  remove: (id: number): Promise<void> =>
    apiFetch<void>(`/api/quote-tandcs/${id}`, { method: "DELETE" }),
};

export const approvalTypesApi = {
  list: (): Promise<ApprovalTypeDto[]> =>
    apiFetch<ApprovalTypeDto[]>("/api/approval-types"),

  get: (code: string): Promise<ApprovalTypeDto> =>
    apiFetch<ApprovalTypeDto>(`/api/approval-types/${encodeURIComponent(code)}`),

  create: (body: CreateApprovalTypeRequest): Promise<ApprovalTypeDto> =>
    apiFetch<ApprovalTypeDto>("/api/approval-types", { method: "POST", body: JSON.stringify(body) }),

  update: (code: string, body: UpdateApprovalTypeRequest): Promise<void> =>
    apiFetch<void>(`/api/approval-types/${encodeURIComponent(code)}`, { method: "PATCH", body: JSON.stringify(body) }),

  remove: (code: string): Promise<void> =>
    apiFetch<void>(`/api/approval-types/${encodeURIComponent(code)}`, { method: "DELETE" }),
};
